import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Pointer, X } from 'lucide-react'
import { HubPreview } from './HubPreview'
import { ProductPreview } from './ProductPreview'

const SHEET_CHROME = 56 + 48 + 56 + 24

function fittedScreenWidth() {
  if (typeof window === 'undefined') return 260
  const byHeight = ((window.innerHeight - SHEET_CHROME - 12) * 393) / 852
  return Math.round(Math.max(200, Math.min(300, window.innerWidth - 48, byHeight)))
}

/** Small-screen entry point that opens the interactive preview sized to fit the viewport. */
export function PreviewLauncher({ sport }) {
  const reduceMotion = useReducedMotion()
  const [open, setOpen] = useState(false)
  const [screenWidth, setScreenWidth] = useState(fittedScreenWidth)
  const triggerRef = useRef(null)
  const closeRef = useRef(null)
  const peekRef = useRef(null)

  useEffect(() => {
    peekRef.current?.setAttribute('inert', '')
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const onResize = () => setScreenWidth(fittedScreenWidth())
    const onKey = (event) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setOpen(false)
    }
    onResize()
    closeRef.current?.focus()
    window.addEventListener('resize', onResize)
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('keydown', onKey, true)
      triggerRef.current?.focus()
    }
  }, [open])

  return (
    <>
      <div className="relative h-[96px] overflow-hidden rounded-[20px] bg-[#181317] shadow-[0_14px_28px_-18px_rgba(7,16,24,0.85)] transition-transform has-[button:active]:scale-[0.98]">
        <div aria-hidden className="absolute inset-0 bg-[linear-gradient(90deg,#22645F_0%,#5C5A5D_55%,#574088_100%)]" />
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(120%_90%_at_20%_0%,rgba(255,255,255,0.14),transparent_60%)]" />

        <div aria-hidden className="relative flex h-full items-center text-white">
          <span ref={peekRef} className="pointer-events-none relative h-full w-[134px] shrink-0 self-stretch min-[380px]:w-[150px]">
            <span className="absolute left-3 top-[14px] origin-top-left -rotate-[5deg] scale-[0.88] min-[380px]:scale-100 drop-shadow-[0_10px_18px_rgba(0,0,0,0.45)]">
              <HubPreview sport={sport} width={120} height={150} interactive={false} />
            </span>
          </span>
          <span className="ml-2 min-w-0 flex-1 whitespace-nowrap">
            <span className="block text-[15px] font-semibold leading-tight min-[380px]:text-[16px]">Try the preview</span>
            <span className="mt-1 block text-[12px] text-white/70">Check what you&apos;ll get</span>
          </span>
          <span className="relative mx-3 flex h-10 w-10 shrink-0 items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-white/35 motion-reduce:animate-none" />
            <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#0C0A09] shadow-[0_6px_14px_-6px_rgba(0,0,0,0.6)]">
              <Pointer className="h-5 w-5" />
            </span>
          </span>
        </div>

        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-label="Try the preview and check what you'll get"
          className="absolute inset-0 z-10 rounded-[20px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
        />
      </div>

      {typeof document !== 'undefined'
        ? createPortal(
            <AnimatePresence>
              {open ? (
                <motion.div
                  role="dialog"
                  aria-modal="true"
                  aria-label="Platform preview"
                  className="fixed inset-0 z-[410] flex flex-col bg-[#F4F6F8] text-[#1c1917]"
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: reduceMotion ? 0 : 16 }}
                  transition={{ duration: 0.26, ease: [0.25, 1, 0.5, 1] }}
                >
                  <div className="flex h-14 shrink-0 items-center justify-end px-4 pt-[env(safe-area-inset-top)]">
                    <button
                      ref={closeRef}
                      type="button"
                      onClick={() => setOpen(false)}
                      aria-label="Close preview"
                      className="-mr-1.5 flex h-11 w-11 items-center justify-center rounded-full text-[#57534e] hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#071018]"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <ProductPreview sport={sport} screenWidth={screenWidth} />
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>,
            document.body,
          )
        : null}
    </>
  )
}
